import {
  FILE_STORE,
  S3_FILES_CACHES,
  UpdateToOnlineStatus,
} from "constants/serviceWorker";
import { iCachedItem } from "interfaces/models/serviceWorker";
import networkMonitor from "libs/network-monitor/NetworkMonitor";
import { refreshCachedData } from "libs/service-worker/refetch-data";
import { getIndexedDb, IndexedDb } from "utils/indexedDb";
import { defaultState, SyncDataOfflineState } from "./type";

class SyncDataOfflineStore {
  private state: SyncDataOfflineState = defaultState;
  private listeners: Set<() => void> = new Set();
  private indexedDB?: IndexedDb;

  constructor(initialState?: Partial<SyncDataOfflineState>) {
    if (initialState) {
      this.state = { ...this.state, ...initialState };
    }

    this.init();
    this.closeRetryModal = this.closeRetryModal.bind(this);
    this.updateStatusRequest = this.updateStatusRequest.bind(this);
    this.openRetryModal = this.openRetryModal.bind(this);
  }

  getState = (): SyncDataOfflineState => {
    return this.state;
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  private setState = (newState: SyncDataOfflineState): void => {
    this.state = { ...newState };

    // Notify all listeners
    this.listeners.forEach((listener) => listener());
  };

  private async init() {
    this.indexedDB = await getIndexedDb();
    if (!this.indexedDB) {
      return;
    }

    const dataFiles: iCachedItem[] = await this.indexedDB.getAll(FILE_STORE);
    const dataStore: iCachedItem[] = await this.indexedDB?.getAll();
    const totalRequest = dataFiles.length + dataStore.length;
    let requestSuccess = 0;
    dataFiles.forEach(
      (item) => item.status === UpdateToOnlineStatus.Success && requestSuccess++
    );
    dataStore.forEach(
      (item) => item.status === UpdateToOnlineStatus.Success && requestSuccess++
    );

    this.setState({ ...this.state, requestSuccess, totalRequest });
  }

  updateStatusRequest(arg: { requestSuccess: number; totalRequest: number }) {
    this.setState({
      ...this.state,
      requestSuccess: arg.requestSuccess,
      totalRequest: arg.totalRequest,
    });
  }

  openRetryModal() {
    this.setState({ ...this.state, isOpenRetryModal: true });
  }

  closeRetryModal() {
    this.setState({ ...this.state, isOpenRetryModal: false });
  }

  async discardOfflineData() {
    if (!this.indexedDB) {
      return;
    }

    const keys = (
      (await this.indexedDB.getAll(FILE_STORE)) as iCachedItem[]
    ).map((item) => item.id);

    // clear file cache
    const cache = await caches.open(S3_FILES_CACHES);
    const cacheKeys = await cache.keys();
    const promiseList: Promise<any>[] = [
      this.indexedDB.emptyStore(FILE_STORE) as any,
    ];

    // only delete cached item not sync success
    const cachedItems = await this.indexedDB.getAll();
    const deleteCachedItemIds: string[] = cachedItems.map((item) => item.id);
    if (deleteCachedItemIds.length) {
      promiseList.push(this.indexedDB.deleteList(deleteCachedItemIds));
    }

    keys.forEach((key) => {
      const request = cacheKeys.find((req) => {
        const reqPathname = decodeURIComponent(
          decodeURIComponent(new URL(req.url).pathname)
        );
        const currentPathname = `/${decodeURIComponent(key)}`;

        return reqPathname === currentPathname;
      });

      if (request) {
        promiseList.push(
          cache.delete(request, {
            ignoreMethod: true,
            ignoreSearch: true,
            ignoreVary: true,
          })
        );
      }
    });

    await networkMonitor.updateStatus(true);
    await Promise.all(promiseList);
  }
}

export const syncDataOfflineStore = new SyncDataOfflineStore();
